<template>
  <div :class="embedded ? 'h-full' : 'h-[calc(100vh-64px)]'" class="flex-1 w-full bg-gray-50 dark:bg-gray-900 overflow-auto">
    <div :class="embedded ? 'w-full' : 'max-w-[22vw] min-w-[280px] mx-auto'" class="p-3 sm:p-4">
      <div v-if="loading" class="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-4">
        <p class="text-sm text-gray-500 dark:text-gray-400">Lade Verlauf ...</p>
      </div>

      <div
        v-else-if="fetchError"
        class="rounded-xl border border-red-200 dark:border-red-900/40 bg-red-50/80 dark:bg-red-950/20 p-4"
      >
        <p class="text-sm text-red-700 dark:text-red-300">{{ fetchError }}</p>
        <UButton class="mt-3" color="neutral" variant="soft" size="xs" @click="fetchDetails">
          Erneut versuchen
        </UButton>
      </div>

      <div
        v-else-if="taskDetails"
        class="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 overflow-hidden"
      >
        <div class="p-4 border-b border-gray-100 dark:border-gray-800">
          <div class="flex flex-wrap items-start justify-between gap-3">
            <div class="min-w-0 flex-1">
              <p class="text-[10px] uppercase tracking-wider font-bold text-gray-400 dark:text-gray-500">Objektnummer</p>
              <p class="text-base font-extrabold text-gray-900 dark:text-white break-words">
                {{ taskDetails.instance.objectNumber || '-' }}
              </p>
            </div>

            <div class="shrink-0 text-right">
              <p class="text-[10px] uppercase tracking-wider font-bold text-gray-400 dark:text-gray-500">Workflow-Ergebnis</p>
              <UBadge
                v-if="taskDetails.instance.workflowState && !['ACTIVE', 'OPEN'].includes(taskDetails.instance.workflowState)"
                :color="workflowResultColor"
                variant="soft"
                size="md"
                class="mt-1 max-w-full whitespace-normal break-words"
              >
                {{ workflowResultLabel }}
              </UBadge>
            </div>
          </div>
        </div>

        <div class="p-4">
          <TaskDetailsHistory :history="taskDetails.instance.history || []" />
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed, onMounted, ref, watch } from 'vue'
import axios from 'axios'
import type { TaskDetailsDto } from '@/types'
import { translateWorkflowState, getWorkflowStateColor } from '@/utils/translations'
import TaskDetailsHistory from '@/components/task/TaskDetailsHistory.vue'

const props = defineProps({
  instanceId: { type: String, required: true },
  embedded: { type: Boolean, default: false },
})

const loading = ref(true)
const fetchError = ref('')
const taskDetails = ref<TaskDetailsDto | null>(null)

const workflowResultLabel = computed(() => {
  const state = taskDetails.value?.instance.workflowState || ''
  return translateWorkflowState(state) || '-'
})

const workflowResultColor = computed(() => {
  return getWorkflowStateColor(taskDetails.value?.instance.workflowState || '')
})

const fetchDetails = async () => {
  loading.value = true
  fetchError.value = ''
  try {
    const response = await axios.get<TaskDetailsDto>(`/api/workflow/v1/tasks/${props.instanceId}`)
    taskDetails.value = response.data
  } catch (error) {
    console.error('Fehler beim Laden der eingebetteten Verlaufsansicht:', error)
    if (axios.isAxiosError(error)) {
      fetchError.value = error.response?.data?.message || 'Verlauf konnte nicht geladen werden.'
    } else {
      fetchError.value = 'Verlauf konnte nicht geladen werden.'
    }
  } finally {
    loading.value = false
  }
}

watch(() => props.instanceId, fetchDetails)
onMounted(fetchDetails)
</script>